import qs from 'qs'
import request from 'api/requestUtils'
import {
  cruiseDealMap,
  cruiseLineMap,
  cruiseFacetsMap,
  cruiseOfferMap,
  cruiseDepartureByIdMap,
  cruiseBookingRateListMap,
  cruiseBookingCabinListMap,
  cruiseBookingCabinSelectionMap,
  cruiseBookingCabinPricingMap,
  cruiseBookingRateDetailsListMap,
  cruiseBookingCabinDetailsListMap,
  cruiseShipMap,
  cruiseOfferSnapshotMap,
  cruiseBookingMap,
  cruiseDealsMap,
} from './mappers/Cruise/cruiseMap'

import { cruiseOfferSummaryMap } from './mappers/Cruise/cruiseOfferSummaryMap'

import { CruisesContract, definitions } from '@luxuryescapes/contract-svc-cruise'
import { offerHasValidDepartures, setCruiseVendorBookingTimer } from 'lib/cruises/cruiseUtils'
import { split } from 'lib/array/arrayUtils'
import { isUuidV4 } from 'lib/string/uuidV4Utils'

export namespace CruiseAPI {
  export type CruiseRateListBody = definitions['RATE_LIST_BODY']
  export type CruiseRateListResponse = definitions['RATE_LIST_RESPONSE']
  export type CruiseCabinRate = definitions['CABIN_RATE']
  export type CruiseCabinRatePackage = definitions['CABIN_RATE_PACKAGE']

  export type CruiseCabinListBody = definitions['CABIN_LIST_BODY']
  export type CruiseCabinListResponse = definitions['CABIN_LIST_RESPONSE']

  export type CruiseRateDetailsListBody = definitions['RATE_CODE_DETAILS_LIST_BODY']
  export type CruiseRateDetailsListResponse = definitions['RATE_CODE_DETAILS_LIST_RESPONSE']

  export type CruiseCabinSelectionBody = definitions['CABIN_SELECTION_BODY']
  export type CruiseCabinSelectionResponse = definitions['CABIN_SELECTION_RESPONSE']

  export type CruiseCabinPricingBody = definitions['CABIN_PRICING_BODY']
  export type CruiseCabinPricingResponse = definitions['CABIN_PRICING_RESPONSE']

  export type CruiseCabinDetailsListBody = definitions['CABIN_DETAILS_LIST_BODY']
  export type CruiseCabinDetailsListResponse = definitions['CABIN_DETAILS_LIST_RESPONSE']

  export type CruiseCabinReleaseBody = definitions['CABIN_RELEASE_BODY']
  export type CruiseCabinReleaseRelease = definitions['CABIN_RELEASE_RESPONSE']

  export type CruiseShipResponse = definitions['SHIP']

  export type CruiseConsolidatedPaymentSchedule = definitions['GET_CONSOLIDATED_PAYMENT_SCHEDULE']

  export type CruiseInclusion = definitions['INCLUSION']

  export type CruiseBooking = definitions['BOOKING_BY_ID']
  export type CruiseOfferSnapshot = definitions['OFFER_SNAPSHOT']
  export type CruiseItinerarySnapshot = definitions['ITINERARY_SNAPSHOT']

  export type CruiseDealBase = definitions['DEAL_RETRIEVAL_BASE']
  export type CruiseDeal = definitions['DEAL_RETRIEVAL']

  export type DepartureByIdResponse = definitions['DEPARTURE']
}

const BASE_URI = '/api/cruise/v1'
const BASE_URI_V2 = '/api/cruise/v2'
const DEFAULT_CABIN_HOLD_TIMEOUT = 900 // 15 minutes
const MAX_NUM_OF_PARALLEL_REQUESTS = 3

export async function getCruiseDealBySlug(slug: string) {
  const response = await request.get<App.ApiResponse<CruiseAPI.CruiseDeal>>(`${BASE_URI_V2}/deals/${slug}`)
  return cruiseDealMap(response.result)
}

export async function getCruiseDeals(
  region: string,
  isActive: boolean = true,
): Promise<Array<App.Cruises.CruiseDealBase>> {
  const params = qs.stringify({
    isActive,
    region,
  })
  const response = await request.get<App.ApiResponse<Array<CruiseAPI.CruiseDealBase>>>(`${BASE_URI_V2}/deals?${params}`)
  return cruiseDealsMap(response.result)
}

export async function getCruiseLineByPageSlug(pageSlug: string) {
  const response = await request.get<App.ApiResponse<CruisesContract.CruiseLine>>(`${BASE_URI_V2}/vendors/slug/${pageSlug}`)
  return cruiseLineMap(response.result)
}

export async function getCruiseSearchFacets(params: App.CruiseSearchFacetParams, region?: string) {
  // destinationIds is an array of strings, but it can also be a string with multiple ids separated by ','
  const destinationIds = params.destinationIds?.flatMap(id => id.split(','))
  const departurePlaceIds = params.departureIds?.flatMap(id => id.split(','))
  const departureIds = params.offerDepartureIds?.flatMap(id => id.split(','))
  const cruiseShips = params.cruiseShips?.flatMap(name => name.split(','))
  const durationRange = params.durationRange?.flatMap(duration => duration.split(','))
  const campaigns = params.campaigns?.flatMap(campaign => campaign.split(',')).join(',')
  const { durationMin, durationMax } = params

  const queryParams = qs.stringify(
    {
      facetTypes: params.facetTypes,
      departureIds,
      destinationPlaceIds: destinationIds,
      departurePlaceIds,
      arrivalPlaceId: params.destinationId,
      cruiseLines: params.cruiseLines,
      departurePlaceId: params.departurePlaceId,
      departureDate: params.departureDate?.substring(0, 7), // gets YYYY-MM
      departureMonths: params.departureMonths?.filter(Boolean),
      departureStartDate: params.departureStartDate,
      departureEndDate: params.departureEndDate,
      rateCodes: params.rateCodes,
      includedFixedFilters: true,
      campaigns,
      ...(durationMin && { durationMin }),
      ...(durationMax && { durationMax }),
      ...(durationRange && { durationRange }),
      ...(region && { region }),
      ...(params.isSpecialOffers && { isSpecialOffers: params.isSpecialOffers }),
      ...(params.isLuxExclusivePromotion && { isLuxExclusivePromotion: params.isLuxExclusivePromotion }),
      ...(params.isLuxPlus && { isLuxPlus: params.isLuxPlus }),
      ...(params.cruiseShips && { cruiseShips }),
      ...(params.minPrice && { minPrice: params.minPrice }),
      ...(params.maxPrice && { maxPrice: params.maxPrice }),
      ...(params.cabinTypes && { cabinTypes: [params.cabinTypes] }),
    },
    { arrayFormat: 'brackets' },
  )

  const { result } = await request
    .get<App.ApiResponse<Array<App.CruiseSearchFacet>>>(`/api/search/cruise/v1/facet/list?${queryParams}`)

  return cruiseFacetsMap(result)
}

function getCruiseSearchQueryString(params: App.OfferListFilters, region?: string) {
  // destinationIds is an array of strings, but it can also be a string with multiple ids separated by ','
  const destinationIds = params.destinationIds?.flatMap(id => id.split(','))
  const departurePlaceIds = params.departureIds?.flatMap(id => id.split(','))
  const departureIds = params.offerDepartureIds?.flatMap(id => id.split(','))
  const durationRange = params.durationRange?.flatMap(duration => duration.split(','))
  const { durationMin, durationMax } = params

  return qs.stringify(
    {
      arrivalPlaceId: params.destinationId,
      departurePlaceId: params.departurePlaceId,
      departureDate: params.departureDate?.substring(0, 7), // gets YYYY-MM
      departureStartDate: params.departureStartDate,
      departureEndDate: params.departureEndDate,
      departureMonths: params.departureMonths?.filter(Boolean),
      cruiseLines: params.cruiseLines,
      rateCodes: params.rateCodes,
      cruiseShips: params.cruiseShips,
      cabinTypes: params.cabinTypes,
      // if a 0 is passed, it's meaningless, so get rid of it
      minPrice: params.minPrice || undefined,
      maxPrice: params.maxPrice || undefined,
      sortBy: params.sortBy,
      campaigns: params.campaigns?.join(','),
      ...(region && { region }),
      ...(durationMin && { durationMin }),
      ...(durationMax && { durationMax }),
      ...(durationRange && { durationRange }),
      ...(params.isSpecialOffers && { isSpecialOffers: params.isSpecialOffers }),
      ...(params.isLuxExclusivePromotion && { isLuxExclusivePromotion: params.isLuxExclusivePromotion }),
      ...(params.isLuxPlus && { isLuxPlus: params.isLuxPlus }),
      destinationName: params.destinationName,
      departureName: params.departureName,
      destinationPlaceIds: destinationIds,
      destinationNames: params.destinationNames,
      departurePlaceIds,
      departureIds,
      departureNames: params.departureNames,
      priceBy: params.priceBy,
    },
    { arrayFormat: 'brackets' },
  )
}

export async function getCruiseSearchList(params: App.OfferListFilters, region?: string) {
  const queryParams = getCruiseSearchQueryString(params, region)
  return request
    .get<App.ApiResponse<Array<string>>>(`/api/search/cruise/v1/list?${queryParams}`)
    .then((response) => response.result.map(id => {
      const isFlashOffer = id.startsWith('tour')
      if (isFlashOffer) {
        return id
      }

      return `cruise:${id}`
    }))
}

export async function getCruiseSearchCount(params: App.OfferListFilters, region?: string) {
  const queryParams = getCruiseSearchQueryString(params, region)
  return request
    .get<App.ApiResponse<number>>(`/api/search/cruise/v1/count?${queryParams}`)
    .then((response) => response.result)
}

export async function getCruiseOfferSummaryBySailingId(id: string, regionCode: string) {
  const response = await request.get<
    App.ApiResponse<CruisesContract.OfferSummary>
  >(`${BASE_URI}/offers/${id}`)

  return cruiseOfferMap(response.result, regionCode)
}

export async function getCruiseOffersBySailingId(ids: Array<string>, regionCode: string) {
  return await Promise.all(ids.map(id =>
    getCruiseOfferSummaryBySailingId(id, regionCode),
  ))
}

export async function getCruiseOfferByIdType(id: string, region: string, currencyCode?: string, allowInvalidOffer?: boolean) {
  if (id.startsWith('cruise-details:')) {
    return getCruiseOffer(id, region, currencyCode, allowInvalidOffer)
  }

  return getCruiseOfferSummary(id, region, currencyCode)
}

export async function getCruiseOffer(id: string, region: string, currencyCode?: string, allowInvalidOffer?: boolean) {
  const idWithoutPrefix = id.replace('cruise-details:', '')
  if (!isUuidV4(idWithoutPrefix)) {
    throw new Error('Invalid cruise offer ID')
  }

  const params = qs.stringify({
    currencyCode,
    region,
    allowInvalidOffer,
  })

  const response = await request.get<
    App.ApiResponse<CruisesContract.Offer>
  >(`${BASE_URI}/offers/${idWithoutPrefix}?${params}`)

  return cruiseOfferMap(response.result, region)
}

export async function getCruiseOfferSummary(id: string, region: string, currencyCode?: string) {
  const idWithoutPrefix = id.replace('cruise:', '')
  if (!isUuidV4(idWithoutPrefix)) {
    throw new Error('Invalid cruise offer ID')
  }

  const params = qs.stringify({
    currencyCode,
    region,
  })

  const response = await request.get<
    App.ApiResponse<CruisesContract.OfferSummaryResponse>
  >(`${BASE_URI}/offers/${idWithoutPrefix}/summary?${params}`)

  return cruiseOfferSummaryMap(response.result, region)
}

export async function getValidCruiseOffer(id: string, region: string, currencyCode?: string) {
  const offer = await getCruiseOffer(id, region, currencyCode)
  const hasValidDepartures = offerHasValidDepartures(offer)

  if (!hasValidDepartures) {
    throw new Error(`No valid departures found for offer ${id}`)
  }

  return offer
}

export async function getCruiseDepartureById(departureId: string) {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.DepartureByIdResponse>
  >(`${BASE_URI_V2}/departures/${departureId}`)

  return cruiseDepartureByIdMap(response.result)
}

// BOOKING FLOW V2
export async function findBookingRateList(params: CruiseAPI.CruiseRateListBody, offer: App.CruiseOffer): Promise<App.Cruises.BookingRateListResponse> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseRateListResponse>, CruiseAPI.CruiseRateListBody>(
    `${BASE_URI_V2}/booking-flow/rate-list`,
    params,
    { credentials: 'include' },
  )

  if (res.errorToRender) return res.errorToRender

  const cabinRateList = cruiseBookingRateListMap(res.result, offer, params.departureId, params.region)
  setCruiseVendorBookingTimer(cabinRateList.cabinHoldTimeout || DEFAULT_CABIN_HOLD_TIMEOUT)

  return cabinRateList
}

export interface BookingCabinListParams {
  componentIds: Array<string>
  sessionId: string
}

export async function findBookingMultipleCabinList(
  params: Omit<CruiseAPI.CruiseCabinListBody, 'componentIds'>,
  componentIds: Array<Array<string>>,
): Promise<Array<App.Cruises.CruiseBookingCabin>> {
  const componentIdsChunked = split(componentIds, MAX_NUM_OF_PARALLEL_REQUESTS)
  let results: Array<App.Cruises.CruiseBookingCabin> = []
  let requestError: App.ApiCallFailure | undefined

  for (let i = 0; i < componentIdsChunked.length; i++) {
    const resultsSubset = await findBookingCabinListSubset(params, componentIdsChunked[i])
      .catch((error) => {
        // Ignore error, it will be handled later if all cabin-list calls fail.
        requestError = error
        return []
      })

    results = [...results, ...resultsSubset.flat()]
  }

  if (requestError && results.length === 0) {
    // If all cabin-list calls fail, throw the error.
    throw requestError
  }

  return results
}

function findBookingCabinListSubset(
  params: Omit<CruiseAPI.CruiseCabinListBody, 'componentIds'>,
  componentIds: Array<Array<string>>,
): Promise<Array<Array<App.Cruises.CruiseBookingCabin>>> {
  return Promise.all(
    componentIds.map((ids) => findBookingCabinList({
      ...params,
      componentIds: ids,
    })),
  )
}

async function findBookingCabinList(params: CruiseAPI.CruiseCabinListBody): Promise<Array<App.Cruises.CruiseBookingCabin>> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinListResponse>, CruiseAPI.CruiseCabinListBody>(
    `${BASE_URI_V2}/booking-flow/cabin-list`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinListMap(res.result)
}

export async function findBookingRateDetailsList(
  params: CruiseAPI.CruiseRateDetailsListBody,
): Promise<Record<string, App.Cruises.BookingRateDetails>> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseRateDetailsListResponse>, CruiseAPI.CruiseRateDetailsListBody>(
    `${BASE_URI_V2}/booking-flow/rate-code-details-list`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingRateDetailsListMap(res.result)
}

export async function findBookingCabinSelection(
  params: CruiseAPI.CruiseCabinSelectionBody,
): Promise<App.Cruises.CruiseBookingCabinSelection> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinSelectionResponse>, CruiseAPI.CruiseCabinSelectionBody>(
    `${BASE_URI_V2}/booking-flow/cabin-selection`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinSelectionMap(res.result)
}

export async function findBookingCabinPricing(
  params: CruiseAPI.CruiseCabinPricingBody,
): Promise<App.Cruises.CruiseBookingCabinPricing> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinPricingResponse>, CruiseAPI.CruiseCabinPricingBody>(
    `${BASE_URI_V2}/booking-flow/cabin-pricing`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinPricingMap(res.result)
}

export async function cruiseBookingCabinRelease(
  params: CruiseAPI.CruiseCabinReleaseBody,
) {
  await request.post<App.ApiResponse<CruiseAPI.CruiseCabinReleaseRelease>, CruiseAPI.CruiseCabinReleaseBody>(
    `${BASE_URI_V2}/booking-flow/cabin-release`,
    params,
    { credentials: 'include' },
  )

  return {
    success: true,
  }
}

export async function findBookingCabinDetailsListMultiple(
  params: Omit<CruiseAPI.CruiseCabinDetailsListBody, 'cabinNumbers'>,
  cabinNumberGroups: Array<Array<string>>,
): Promise<App.Cruises.BookingCabinDetailsList> {
  const results = await Promise.all(
    cabinNumberGroups.map((cabinNumbers) => findBookingCabinDetailsList({
      ...params,
      cabinNumbers,
    })),
  )

  return results.reduce((acc, res) => ({ ...acc, ...res }), {})
}

export async function findBookingCabinDetailsList(
  params: CruiseAPI.CruiseCabinDetailsListBody,
): Promise<App.Cruises.BookingCabinDetailsList> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinDetailsListResponse>, CruiseAPI.CruiseCabinDetailsListBody>(
    `${BASE_URI_V2}/booking-flow/cabin-details-list`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinDetailsListMap(res.result)
}

export async function findCruiseShip(id: string) {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.CruiseShipResponse>
  >(`${BASE_URI_V2}/ships/${id}`)

  return cruiseShipMap(response.result)
}

export function getConsolidatedPaymentSchedule(
  bookingIds: Array<string>,
): Promise<CruiseAPI.CruiseConsolidatedPaymentSchedule> {
  const queryParams = qs.stringify({ bookingIds })

  return request.get<
    App.ApiResponse<CruiseAPI.CruiseConsolidatedPaymentSchedule>
  >(`${BASE_URI_V2}/payment-schedules/consolidated?${queryParams}`, { credentials: 'include' })
    .then((response) => response.result)
}

export async function getCruiseOfferSnapshot(
  bookingId: string,
): Promise<App.Cruises.CruiseOfferSnapshot> {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.CruiseOfferSnapshot>
  >(`${BASE_URI_V2}/bookings/${bookingId}/snapshot/offer`, { credentials: 'include' })

  return cruiseOfferSnapshotMap(response.result)
}

export async function getCruiseBookingById(
  bookingId: string,
  regionCode: string,
): Promise<App.Cruises.CruiseBooking> {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.CruiseBooking>
  >(`${BASE_URI_V2}/bookings/${bookingId}`, { credentials: 'include' })

  return cruiseBookingMap(response.result, regionCode)
}
